import { redirect, defer } from 'react-router-dom';
import { getEulaForMfaService } from 'services/eula';
import {
  getPostFlowUrl,
  getReturnUrlFromRequestLoader,
} from 'utils/common/route';
import { checkUserToken } from 'utils/author';
import { getData } from 'utils/storage';
import { getWalmartAuthorizationParams } from 'hooks/router';
import storeApp from 'store/storeApp';
import { ensureUserInfoLoaded } from 'utils/loaderUtils';
import { queryBrandingNavigation } from 'hooks/useGetBranding';
import { ensureEulaData } from 'pages/eula/useEulaQuery';
import { updateMfaLoginInfo } from '@redux/user/actions';

const getUserId = (userInfo) => {
  return userInfo?.id;
};
export const eulaLoader =
  (queryClient) =>
  async ({ request }) => {
    const returnUrl = getReturnUrlFromRequestLoader(request);
    const { walmartSearchQuery } = getWalmartAuthorizationParams(request);

    const state = storeApp.getState();
    const dispatch = storeApp.dispatch;

    const mfaLoginInfo = state?.user?.mfaLoginInfo;
    let userInfo = null;

    const isUserLoggedIn = checkUserToken();
    const impersonateData = getData('impersonate');

    // Handle authentication redirects
    if (!mfaLoginInfo && !isUserLoggedIn) {
      if (walmartSearchQuery) {
        throw redirect(`/login${walmartSearchQuery}`);
      } else {
        throw redirect(`/login?returnUrl=${encodeURIComponent(returnUrl)}`);
      }
    }

    const { loginSession } = mfaLoginInfo || {};

    let eulaData = null;
    let eulaError = null;

    if (isUserLoggedIn) {
      // Clear any stale mfaLoginInfo immediately
      if (mfaLoginInfo) {
        dispatch(updateMfaLoginInfo(null));
      }
    }

    // ✅ MFA Flow - DON'T load userInfo yet (user not fully authenticated)
    if (loginSession && !isUserLoggedIn) {
      try {
        const response = await getEulaForMfaService(loginSession);
        const { isSuccess, data } = response;

        if (isSuccess) {
          eulaData = {
            eula: data,
            eulaContent: data.eulaContent,
            type: 'mfa',
          };
        } else {
          const redirectUrl = getPostFlowUrl({
            type: 'eula-for-mfa',
            returnUrl,
            dispatch,
          });
          throw redirect(redirectUrl);
        }
      } catch (error) {
        if (error?.status === 302 || error instanceof Response) {
          throw error;
        }
        eulaError = 'not-exist';
      }

      return {
        eulaData,
        eulaContent: eulaData?.eulaContent,
        eulaError,
        returnUrl,
        walmartSearchQuery,
        mfaLoginInfo,
        userInfo: {},
        impersonateData,
        isUserLoggedIn,
        isAccepted: eulaData?.eula?.isAccepted,
        isLoading: false,
        brandingPromise: null,
        redirectUrl: null,
      };
    }

    // ✅ Authorized User Flow - NOW load userInfo using React Query
    if (isUserLoggedIn && !loginSession) {
      try {
        // ✅ Load userInfo using queryClient (React Query)
        const { userInfo: loadedUserInfo } = await ensureUserInfoLoaded({
          queryClient,
          returnUrl,
        });
        userInfo = loadedUserInfo;

        const userId = getUserId(userInfo);
        const eulaQueryData = await ensureEulaData(queryClient, userId);

        // No active EULA for this user means there is nothing to accept
        if (!eulaQueryData) {
          const nextUrl = getPostFlowUrl({
            type: 'eula',
            returnUrl,
            dispatch,
          });
          throw redirect(nextUrl);
        } else {
          const isAccepted = eulaQueryData?.isAccepted;

          eulaData = {
            eula: eulaQueryData,
            eulaContent: eulaQueryData.eulaContent,
            type: 'authorized',
          };

          const redirectUrl = getPostFlowUrl({
            type: 'eula',
            returnUrl,
            dispatch,
          });

          if (isAccepted) {
            const brandingPromise = queryBrandingNavigation({
              queryClient,
              userId,
            })
              .then((brandingData) => {
                return {
                  brandingData,
                  shouldNavigate: true,
                  navigationUrl: redirectUrl,
                  completed: true,
                };
              })
              .catch((error) => {
                return {
                  brandingData: null,
                  shouldNavigate: true,
                  navigationUrl: redirectUrl,
                  completed: true,
                  error: error.message,
                };
              });

            return defer({
              eulaData,
              eulaContent: eulaData?.eulaContent,
              eulaError,
              returnUrl,
              walmartSearchQuery,
              mfaLoginInfo,
              userInfo,
              impersonateData,
              isUserLoggedIn,
              isAccepted: true,
              isLoading: true,
              brandingPromise,
              redirectUrl,
            });
          } else {
            const brandingPromise = queryBrandingNavigation({
              queryClient,
              userId,
            })
              .then((brandingData) => {
                return {
                  brandingData,
                  shouldNavigate: false,
                  navigationUrl: null,
                  completed: true,
                };
              })
              .catch((error) => {
                return {
                  brandingData: null,
                  shouldNavigate: false,
                  navigationUrl: null,
                  completed: true,
                  error: error.message,
                };
              });

            return defer({
              eulaData,
              eulaContent: eulaData?.eulaContent,
              eulaError,
              returnUrl,
              walmartSearchQuery,
              mfaLoginInfo,
              userInfo,
              impersonateData,
              isUserLoggedIn,
              isAccepted: false,
              isLoading: true,
              brandingPromise,
              redirectUrl: null,
            });
          }
        }
      } catch (error) {
        if (error?.status === 302 || error instanceof Response) {
          throw error;
        }
        eulaError = 'not-exist';
      }
    }

    // Return fallback data for errors
    return {
      eulaData,
      eulaContent: eulaData?.eulaContent,
      eulaError,
      returnUrl,
      walmartSearchQuery,
      mfaLoginInfo,
      userInfo,
      impersonateData,
      isUserLoggedIn,
      isAccepted: eulaData?.eula?.isAccepted || false,
      isLoading: false,
      brandingPromise: null,
      redirectUrl: null,
    };
  };
